/* =============================================================================
   VIEWS/ADMIN — MAIL-OPT-IN-BLOCK (REF-7b, S75)
   =============================================================================
   Wiederverwendbarer Render-Helper + State-Logik für das Cockpit-Mail-Opt-In:
   E-Mail-Feld + 4 Toggles, geteilt von Order-Erfassung (admin-order-entry*) und
   Reservierungs-Erfassung (walkin.js). DRY statt 4× dupliziert.

   Hintergrund: Seit Phase 73c senden Cockpit-Pfade Auto-Mails NUR, wenn die
   jeweilige `mail_opt_in_<kind>`-Spalte true ist. Dieser Block erfasst die
   Operator-Zustimmung. Default: Auto-AN bei E-Mail-Eingabe für die positiven
   Lifecycle-Kinds (confirmed/ready bzw. confirmed/reminder), negative bleiben
   aus; Operator kann alles überstimmen (`_touched`-Flag). Leere E-Mail → alle
   aus + Checkboxen disabled.

   Inline-Handler gehen über dom-bindings.js (CSP): KEIN window.func(), nur
   func() — daher werden Handler-Namen als String übergeben.
============================================================================ */

import { htmlEscape } from '../../utils/helpers.js';

/** Mail-Kinds + Klartext-Labels pro Formular-Typ (Trigger-Gating aus Phase 73c). */
export const ORDER_MAIL_KINDS = [
  { key: 'confirmed', label: 'Bestellung angenommen' },
  { key: 'ready',     label: 'Bereit zur Abholung / Lieferung' },
  { key: 'cancelled', label: 'Storno-Benachrichtigung' },
  { key: 'receipt',   label: 'Beleg-/Rechnungsversand' },
];
export const ORDER_POSITIVE_KINDS = ['confirmed', 'ready'];

export const RESERVATION_MAIL_KINDS = [
  { key: 'confirmed', label: 'Reservierung bestätigt' },
  { key: 'declined',  label: 'Reservierung abgelehnt' },
  { key: 'cancelled', label: 'Storno-Benachrichtigung' },
  { key: 'reminder',  label: 'Erinnerung (Tag vorher)' },
];
export const RESERVATION_POSITIVE_KINDS = ['confirmed', 'reminder'];

/** Frischer Opt-In-State (alle Kinds false + `_touched=false`). */
export function freshOptInState(kinds) {
  const out = { _touched: false };
  for (const k of kinds) out[k.key] = false;
  return out;
}

/**
 * Pure: berechnet den Opt-In-State neu in Abhängigkeit von der E-Mail.
 * - E-Mail leer  → alle Kinds false (egal ob _touched).
 * - E-Mail da + noch nicht vom Operator angefasst (`_touched=false`) →
 *   positive Kinds true, Rest false.
 * - E-Mail da + `_touched=true` → unverändert (Operator-Override gewinnt).
 * Liefert ein NEUES Objekt (mutiert das Eingabe-Objekt nicht).
 */
export function applyEmailAutoDefaults(optIn, email, positiveKinds) {
  const hasEmail = !!(email && String(email).trim());
  const out = { ...optIn };
  const keys = Object.keys(out).filter(k => k !== '_touched');
  if (!hasEmail) {
    for (const k of keys) out[k] = false;
    return out;
  }
  if (out._touched) return out;
  const positive = new Set(positiveKinds || []);
  for (const k of keys) out[k] = positive.has(k);
  return out;
}

/**
 * Render des Mail-Opt-In-Blocks als HTML-String.
 * @param {object}   opts
 * @param {Array}    opts.kinds        z.B. ORDER_MAIL_KINDS
 * @param {string}   opts.email        aktueller E-Mail-Wert
 * @param {object}   opts.optIn        State {<kind>:bool, _touched:bool}
 * @param {string}   opts.idPrefix     eindeutiges Präfix für Element-IDs + data-Attr
 * @param {string}   opts.emailHandler Name der window-gebundenen oninput-Funktion
 * @param {string}   opts.toggleHandler Name der window-gebundenen onchange-Funktion
 * @param {string}  [opts.title]
 */
export function renderMailOptInBlock(opts) {
  const {
    kinds, email = '', optIn = {}, idPrefix,
    emailHandler, toggleHandler,
    title = '📧 Automatische E-Mails an den Gast',
    // S88 C: Cockpit-Order/-Reservierung haben die Opt-in-Checkboxen entfernt —
    // Transaktionsmails brauchen keine separate Einwilligung, der Server leitet
    // mail_opt_in_* aus der E-Mail-Praesenz ab (phase_84). `showToggles:false`
    // rendert nur das E-Mail-Feld. `emailRequired:true` markiert es als Pflicht
    // (Delivery + Reservierung). Reservierungs-Walk-In (Reservierung) nutzt das
    // weiterhin mit Checkboxen NUR, wenn ein Caller showToggles nicht setzt.
    showToggles = true,
    emailRequired = false,
  } = opts || {};
  const hasEmail = !!(email && String(email).trim());
  const disabledAttr = hasEmail ? '' : 'disabled';

  const checks = showToggles ? (kinds || []).map(k => `
      <label class="checkbox" style="display:flex;align-items:center;gap:6px;${hasEmail ? '' : 'opacity:.45;'}">
        <input type="checkbox" id="${idPrefix}-mo-${k.key}" ${optIn[k.key] ? 'checked' : ''} ${disabledAttr}
               onchange="${toggleHandler}('${k.key}', this.checked)">
        <span style="font-size:.88rem;">${htmlEscape(k.label)}</span>
      </label>`).join('') : '';

  const emailLabel = emailRequired ? 'E-Mail *' : 'E-Mail (optional)';
  const reqAttr = emailRequired ? 'required' : '';

  // Hinweistext: erklaert, was die E-Mail bewirkt (Checkboxen sind weg).
  let hint;
  if (showToggles) {
    hint = hasEmail ? '' : '<div style="font-size:.78rem;color:var(--ink-soft,#888);margin-top:6px;">Ohne E-Mail werden keine automatischen Mails versendet.</div>';
  } else if (hasEmail) {
    hint = '<div style="font-size:.78rem;color:var(--ink-soft,#888);margin-top:6px;">Der Gast erhält automatische Benachrichtigungen (Bestätigung, fertig, Storno, Beleg).</div>';
  } else if (emailRequired) {
    hint = '<div style="font-size:.78rem;color:var(--danger,#c0392b);margin-top:6px;">E-Mail erforderlich — der Gast erhält automatische Benachrichtigungen.</div>';
  } else {
    hint = '<div style="font-size:.78rem;color:var(--ink-soft,#888);margin-top:6px;">Ohne E-Mail keine automatischen Benachrichtigungen.</div>';
  }

  return `
    <div class="mail-optin-block" data-mailoptin="${idPrefix}" style="margin:10px 0;padding:10px;border:1px solid var(--border);border-radius:8px;">
      <div class="field">
        <label for="${idPrefix}-email">${emailLabel}</label>
        <input id="${idPrefix}-email" type="email" ${reqAttr} value="${htmlEscape(email)}"
               placeholder="gast@example.com" oninput="${emailHandler}(this.value)">
      </div>
      ${showToggles ? `<div style="font-size:.82rem;color:var(--ink-soft,#888);margin:8px 0 4px;">${htmlEscape(title)}</div>
      <div style="display:flex;flex-direction:column;gap:5px;">${checks}</div>` : ''}
      ${hint}
    </div>`;
}

/**
 * Aktualisiert NUR die Checkboxen (checked + disabled + Label-Opacity) per
 * direktem DOM-Zugriff — OHNE das E-Mail-Input neu zu rendern. So bleibt beim
 * Tippen der Focus/Cursor erhalten (Memory `gg-non-destruktive-renders`).
 * Laufzeit-No-Op unter Node (kein document) → testfreundlich.
 */
export function syncMailOptInCheckboxes(idPrefix, optIn, hasEmail) {
  if (typeof document === 'undefined' || !optIn) return;
  const keys = Object.keys(optIn).filter(k => k !== '_touched');
  for (const k of keys) {
    const cb = document.getElementById(`${idPrefix}-mo-${k}`);
    if (!cb) continue;
    cb.checked = !!optIn[k];
    cb.disabled = !hasEmail;
    const label = cb.closest ? cb.closest('label') : null;
    if (label) label.style.opacity = hasEmail ? '' : '.45';
  }
}
